import React from 'react';
import { View } from 'react-native';

import type { PlanGroupProfile, PlanParticipantProfile } from '@components/Plan/Details';
import PlanSummary from '@components/Plan/Summary';
import { ViewPlanScreenToggleState } from '@navigation/Types';
import type { Plan } from '@types';

export type PlanCardProps = Omit<React.ComponentProps<typeof View>, 'children'> & {
  /** Plan rendered by the card. */
  data: Plan;
};

type PlanCardOpenPlanOptions = {
  toggleState?: ViewPlanScreenToggleState;
};

export type PlanCardNavigationCallbacks = {
  /** Opens the plan detail screen. */
  onPlanPress: (planId: string, options?: PlanCardOpenPlanOptions) => void;
  /** Opens a user's profile from card avatars. */
  onUserPress: (userId: string) => void;
  /** Opens a chat from the card action row. */
  onChatPress: (chatId: string) => void;
};

type LargePlanCardSummaryProps = Pick<
  React.ComponentProps<typeof PlanSummary>,
  | 'actionButtonStyle'
  | 'actionContentContainerStyle'
  | 'avatarLimit'
  | 'avatarSize'
  | 'detailIconSize'
  | 'detailTextNumberOfLines'
  | 'detailTextStyle'
  | 'detailsStyle'
  | 'infoContainerStyle'
  | 'rowStyle'
  | 'showCommentAction'
>;

export type LargePlanCardProps = PlanCardProps &
  PlanCardNavigationCallbacks & {
    /** Loaded groups associated with the plan. */
    groupProfiles?: PlanGroupProfile[];
    /** Overrides the default card press behavior; pass null to make the card body non-pressable. */
    onPress?: (() => void) | null;
    /** Loaded participant profiles for the plan owner and joined users. */
    participantUsers?: PlanParticipantProfile[];
  } & LargePlanCardSummaryProps;

export function LargePlanCard({
  actionButtonStyle,
  actionContentContainerStyle,
  avatarLimit,
  avatarSize,
  data: planDoc,
  detailIconSize,
  detailTextNumberOfLines,
  detailTextStyle,
  detailsStyle,
  onPress,
  onChatPress,
  onPlanPress,
  onUserPress,
  infoContainerStyle,
  rowStyle,
  style,
  groupProfiles = [],
  participantUsers = [],
  showCommentAction,
  ...props
}: LargePlanCardProps) {
  const cardPressHandler =
    onPress === null ? undefined : (onPress ?? (() => onPlanPress(planDoc.planId)));

  return (
    <PlanSummary
      actionButtonStyle={actionButtonStyle}
      actionContentContainerStyle={actionContentContainerStyle}
      avatarLimit={avatarLimit}
      avatarSize={avatarSize}
      detailIconSize={detailIconSize}
      detailTextNumberOfLines={detailTextNumberOfLines}
      detailTextStyle={detailTextStyle}
      detailsStyle={detailsStyle}
      groupProfiles={groupProfiles}
      infoContainerStyle={infoContainerStyle}
      onAvatarPress={onUserPress}
      onChat={onChatPress}
      onCommentPress={() =>
        onPlanPress(planDoc.planId, {
          toggleState: ViewPlanScreenToggleState.COMMENTS,
        })
      }
      onPress={cardPressHandler}
      participantUsers={participantUsers}
      plan={planDoc}
      rowStyle={rowStyle}
      showCommentAction={showCommentAction}
      style={style}
      testID={planDoc.planId}
      {...props}
    />
  );
}
